import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { LineReveal, FadeIn } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import ChapterHeader from "@/components/ChapterHeader";
import KenBurnsHero from "@/components/KenBurnsHero";
import SeasonalWallpaper from "@/components/SeasonalWallpaper";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import { useCategoryPhotos, pickPhoto, pickPhotoByTitle } from "@/lib/usePhotos";
import { ArrowUpRight } from "lucide-react";

export default function Home() {
  const heroRef = useRef(null);
  const heritage = useCategoryPhotos("heritage");
  const orchard = useCategoryPhotos("orchard");
  const activity = useCategoryPhotos("activity");
  const dining = useCategoryPhotos("dining");

  // Ken-Burns hero: use up to first 4 heritage photos, else render nothing (KenBurnsHero returns null on empty)
  const heroImages = [...heritage || [], ...orchard || []].slice(0, 4).map((p) => p.url);

  const orchardImg = pickPhoto(orchard, 0);
  const produceImg = pickPhoto(orchard, 1);
  const tandoorImg = pickPhoto(dining, 0);
  const lodgeImg = pickPhoto(heritage, 1);
  const jeepImg = pickPhoto(activity, 0);
  const billiardsImg = pickPhotoByTitle(activity, "billiards", 1);
  const dartsImg = pickPhotoByTitle(activity, "darts", 2);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.3]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100vh] overflow-hidden">
        <motion.div
          style={{ y, scale, opacity }}
          className="absolute inset-0 h-[120vh]"
        >
          <KenBurnsHero images={heroImages} interval={6500} className="w-full h-full" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1C1913]/70 via-[#1C1913]/20 to-[#1C1913]" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-[1600px] px-6 lg:px-12 pt-40 pb-24">
          <FadeIn delay={0.2} y={0}>
            <div className="flex items-center gap-4 mb-10">
              <span className="stamp">Est · Circa 1930's</span>
              <span className="stamp hidden md:inline-flex">Ranchi · Jharkhand</span>
            </div>
          </FadeIn>

          <h1
            className="font-display text-[3.5rem] sm:text-[5rem] md:text-[7rem] lg:text-[9rem] leading-[0.92] tracking-tighter text-[#F2E7D0]"
            style={{
              textShadow:
                "0 2px 12px rgba(0,0,0,0.55), 0 8px 40px rgba(0,0,0,0.35)",
            }}
          >
            <LineReveal delay={0.3}>Tikratoli</LineReveal>
            <LineReveal delay={0.5} className="italic text-[#F2E7D0]/90">Farm Resorts</LineReveal>
          </h1>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
            <FadeIn delay={1.1} className="md:col-span-6">
              <p className="text-[#F2E7D0]/85 text-xl md:text-2xl lg:text-3xl leading-snug font-display max-w-2xl">
                A working farm, a heritage home, a club, banquet hall & a resort — set amidst the birds and the bees.
              </p>
            </FadeIn>
            <FadeIn delay={1.25} className="md:col-span-4 md:col-start-8">
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/booking"
                  data-testid="hero-reserve-btn"
                  className="inline-flex items-center gap-3 bg-[#D97645] hover:bg-[#C0602F] transition text-[#F2E7D0] px-6 py-4 font-mono-label"
                >
                  Reserve a stay <ArrowUpRight size={16} />
                </Link>
                <Link
                  to="/heritage"
                  data-testid="hero-explore-btn"
                  className="inline-flex items-center gap-3 border border-[#F2E7D0]/40 hover:border-[#F2E7D0] transition text-[#F2E7D0] px-6 py-4 font-mono-label"
                >
                  The story
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>

        <div className="absolute bottom-8 left-0 right-0 z-10 mx-auto max-w-[1600px] px-6 lg:px-12 flex items-center justify-between font-mono-label text-[#F2E7D0]/60">
          <span>N · 23.35°  E · 85.33°</span>
          <span className="hidden md:block">↓ scroll — the estate awaits</span>
          <span>Vol · I</span>
        </div>
      </section>

      {/* MARQUEE */}
      <EditorialMarquee items={["Mango Orchard", "WWII Jeeps · 1942", "Private Sal Forest", "Tandoor & Continental", "Billiards"]} />

      {/* MANIFESTO / CHAPTER 01 */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-32 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeIn className="lg:col-span-5">
            <ChapterHeader
              num="01"
              kicker="The Estate"
              title={<>Not a resort <em className="italic text-[#D97645]">built</em> around a farm — a farm that <em className="italic">became</em> a resort.</>}
            />
            <p className="text-[#B8A891] text-lg leading-relaxed max-w-md">
              Vegetables from the kitchen garden. Milk from our own dairy. Mango, lychee, guava — plucked
              the morning they arrive on your plate. The chain from soil to table is short here, and
              always has been.
            </p>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-7 grid grid-cols-6 gap-4">
            <div className="col-span-4 aspect-[4/5] bg-[#27221C] overflow-hidden">
              {orchardImg && <img src={orchardImg} alt="Mango orchard" className="w-full h-full object-cover vintage-img" />}
            </div>
            <div className="col-span-2 flex flex-col gap-4">
              <div className="aspect-square bg-[#27221C] overflow-hidden">
                {produceImg && <img src={produceImg} alt="Fresh produce" className="w-full h-full object-cover vintage-img" />}
              </div>
              <div className="aspect-[3/4] bg-[#27221C] overflow-hidden">
                {tandoorImg && <img src={tandoorImg} alt="Tandoor cuisine" className="w-full h-full object-cover vintage-img" />}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CHAPTER 02 - HERITAGE LODGE */}
      <section className="bg-[#27221C] py-32 md:py-40">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeIn className="lg:col-span-7 order-2 lg:order-1">
            <div className="w-full aspect-[4/3] bg-[#27221C] overflow-hidden">
              {lodgeImg && <img src={lodgeImg} alt="Heritage lodge" className="w-full h-full object-cover vintage-img" />}
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-5 order-1 lg:order-2 flex flex-col justify-center">
            <ChapterHeader
              num="02"
              kicker="Heritage Home"
              title={<>A British hunting lodge — <em className="italic">exuding</em> colonial charm for the last 100 years.</>}
            />
            <p className="text-[#B8A891] text-lg leading-relaxed">
              The heritage home was built as a British hunting lodge with a functioning fireplace,
              serving Indian and Continental cuisine since the mid-1930's — and continues to this day.
              Alongside it stands the Buddhist Residential Block — the first of its kind in Ranchi,
              Jharkhand.
            </p>
            <Link to="/heritage" data-testid="home-heritage-link" className="mt-8 inline-flex items-center gap-2 link-underline font-mono-label text-[#D97645] w-fit">
              Read the full history <ArrowUpRight size={14} />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* CHAPTER 03 - WWII JEEPS */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-32 md:py-40">
        <ChapterHeader
          num="03"
          kicker="Adventure Rides"
          title={<>Classic World War II jeeps & all-terrain vehicles.</>}
          sub="Original 1942 open-top Willys jeeps — the same models that rolled through the Second World War — alongside ATVs and bicycles for the orchard trails."
        />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <FadeIn className="md:col-span-8">
            <div className="w-full aspect-[16/9] bg-[#27221C] overflow-hidden">
              {jeepImg && <img src={jeepImg} alt="1942 WWII Willys Jeep" className="w-full h-full object-cover vintage-img" />}
            </div>
          </FadeIn>
          <FadeIn delay={0.1} className="md:col-span-4 flex flex-col gap-6 justify-between">
            <div className="border border-white/10 p-6">
              <div className="chapter-num mb-2">Fleet · 01</div>
              <div className="font-display text-4xl text-[#F2E7D0]">1942 Willys GPW</div>
              <div className="mt-2 text-sm text-[#B8A891]">Left-hand drive · Open top · Petrol · Original.</div>
            </div>
            <div className="border border-white/10 p-6">
              <div className="chapter-num mb-2">Adventure</div>
              <div className="font-display text-4xl text-[#F2E7D0]">ATVs & Cycles</div>
              <div className="mt-2 text-sm text-[#B8A891]">All-terrain rides and bicycles for the orchard trails.</div>
            </div>
            <Link to="/activities" data-testid="home-activities-link" className="link-underline font-mono-label text-[#D97645] w-fit inline-flex items-center gap-2">
              See all experiences <ArrowUpRight size={14} />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* SEASONAL INTERLUDE — auto-switches based on current month */}
      <SeasonalWallpaper />

      {/* CHAPTER 04 - GAMES & LIBRARY */}
      <section className="bg-[#27221C] py-32">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <ChapterHeader num="04" kicker="Indoors" title={<>Billiards, snooker, table tennis, carrom, darts — and books, going back several decades.</>} />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <FadeIn className="md:col-span-7">
              <div className="w-full aspect-[4/3] bg-[#27221C] overflow-hidden">
                {billiardsImg && <img src={billiardsImg} alt="Billiards room" className="w-full h-full object-cover vintage-img" />}
              </div>
            </FadeIn>
            <FadeIn delay={0.1} className="md:col-span-5">
              <div className="w-full aspect-[4/3] bg-[#27221C] overflow-hidden">
                {dartsImg && <img src={dartsImg} alt="Darts board" className="w-full h-full object-cover vintage-img" />}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS WALL */}
      <section className="bg-[#1C1913] border-t border-[#3A3229] py-24 md:py-32" data-testid="home-reviews">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <FadeIn className="md:col-span-4">
              <span className="chapter-num">Ch. 05 · Word of mouth</span>
              <h2 className="mt-4 font-display text-5xl md:text-7xl leading-[0.95] text-[#F2E7D0]">
                Loved on <em className="italic text-[#D97645]">Google</em>.
              </h2>
              <p className="mt-6 text-[#B8A891] text-lg leading-relaxed max-w-sm">
                Every review is verified by Google. Refreshed monthly.
              </p>
            </FadeIn>
            <FadeIn delay={0.15} className="md:col-span-8">
              <GoogleReviewsCarousel variant="full" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* CHAPTER 06 - CTA / STAY */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-32 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
          <FadeIn className="md:col-span-8">
            <span className="chapter-num">Ch. 06</span>
            <h2 className="mt-4 font-display text-6xl md:text-8xl leading-[0.95] text-[#F2E7D0]">
              Come <em className="italic text-[#D97645]">stay</em> — the fire is
              already lit.
            </h2>
          </FadeIn>
          <FadeIn delay={0.1} className="md:col-span-4">
            <p className="text-[#B8A891] mb-6">Reserve a room in the heritage lodge, the Buddhist block, or a standalone orchard cottage.</p>
            <Link to="/booking" data-testid="home-cta-book" className="inline-flex items-center gap-3 bg-[#D97645] hover:bg-[#C0602F] transition text-[#F2E7D0] px-8 py-5 font-mono-label">
              Begin reservation <ArrowUpRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
